import { MagentoPayment, MagentoProduct } from "libs/magento/src/lib/models";
import { SapJaws, SapShippingCostItem } from "libs/sap/src/lib/models/sap.model";
import { DataTransferMethod } from "libs/treatments/src/lib/models/treatment.model";

// interface to the corresponding Product schema in database
export interface ProductModel {
	name?: string;
	key: string;
	productConfigs: ProductConfig[];
	isSetupProduct: boolean;
	description?: string;
	disallowedCountries?: string[];
	disallowedWms?: string[];

	/** List of wms that override the country disallowance */
	exceptionallyAllowedWms?: string[];

	lastModified?: Date;
	created?: Date;

	/** Flag indicating whether the product has explict configs for upper+lower bundle configs. */
	hasBundleConfigs: boolean;
}

export interface ProductConfig {
	jaws: string;
	method: string;
	range?: ToothArea;
	items: ProductItem[];
}

export interface ProductItem {
	itemCode: string;
	quantity: number;
	jaws: SapJaws;
	steps?: string;
	range?: ToothArea;
}

/**@deprecated */
export interface ProductInfoMagento {
	/** @deprecated */
	urlKey: string; // mage url key of category

	// Only present or at all relevant in Aligner products pre PROFESSIONAL:
	/** @deprecated */
	steps?: number[];
	/** @deprecated */
	corrections?: number[];
	/** @deprecated */
	title?: string;
	/** @deprecated */
	toothArea?: string;

	// DEPRECATED or/and may be removed in future:
	/** @deprecated */
	brand?: string; // al
	/** @deprecated */
	line?: string; // product url key part before first dash eg.: alca2
	/** @deprecated */
	level?: string; // product url key without first part eg.: professionallite
	/** @deprecated */
	description?: string;
	/** @deprecated Unused */
	alignerPerStep?: number;
	/** @deprecated Only relevant order process. To be removed in #186 */
	categoryId?: number; // mage category id
	/** @deprecated Unused since #386 (v1.10.0). */
	image?: string;
	/** @deprecated Only used as a flag since #386 (v1.10.0).  */
	mainCategoryLabel?: string;
	/** @deprecated  Only relevant order process. Should not be accessed later.*/
	price?: number;
}
export interface ProductOptions {
	dataTransfer: DataTransferMethod;
	uj: ToothArea;
	lj: ToothArea;
	withPrintService?: boolean;
	/**@deprecated */
	custom?: CustomProductOptions;
	/**@deprecated */
	dataUpload?: boolean;
}

/**@deprecated */
export interface CustomProductOptions {
	zahnbereich?: {
		uj?: {
			value: string; // e.g. "2-2", "3-3", "4-4"
		};
		lj?: {
			value: string; // e.g. "2-2", "3-3", "4-4"
		};
	};

	/** @deprecated LEGACY (individua). Don't use. */
	specification?: any;
}

/** Related to DataTransferMethod, but describes whether the models are physical or digital */
export enum DataTransferType {
	DIGITAL_UPLOAD = "Upload",
	PHYSICAL_MODELS = "Modelle",
}

export const DataTypeForTransferMethod: Record<DataTransferMethod, DataTransferType> = {
	[DataTransferMethod.UPLOAD]: DataTransferType.DIGITAL_UPLOAD,
	[DataTransferMethod.UPS]: DataTransferType.PHYSICAL_MODELS,
	[DataTransferMethod.OTHER]: DataTransferType.PHYSICAL_MODELS,
};

export interface Product {
	key: ProductKey;
	options: Partial<ProductOptions>;
	handling?: {
		shipment?: SapShippingCostItem;
		shippingAddressCode?: string;
		billingAddressCode?: string;
		/**@deprecated */
		payment?: MagentoPayment;
	};

	/**@deprecated with version 2.1.0 */
	//only used by some legacy cases
	info?: ProductInfoMagento;

	/**@deprecated */
	mainArticles?: MagentoProduct[];
	/**@deprecated */
	stepArticles?: MagentoProduct[];
	/**@deprecated */
	corrArticles?: MagentoProduct[];
}

export type ToothAreaCombined = {
	UJ: ToothArea;
	LJ: ToothArea;
};

export enum ToothArea {
	NONE = "",
	TWO_TWO = "2-2",
	THREE_THREE = "3-3",
	FOUR_FOUR = "4-4",
	FIVE_FIVE = "5-5",
	SIX_SIX = "6-6",
	SEVEN_SEVEN = "7-7",
}

/**@deprecated */
export enum ProductBrand {
	ALIGNER = "ca",
	MEMOTAIN = "mt",
	RETENTIONSSCHIENE = "retentionsschiene",
	LEGACY_ALIGNER_PRO = "cap", // LEGACY (never seen in database)
}

/**@deprecated */
export enum ProductLine {
	DATEN = "daten",
	SCHIENE = "schiene",
	CAM = "cam", // MEMOTAIN
	LEGACY_PROFESSIONAL = "professional", // LEGACY (last modified treatment on 2020-06-02)
}

/**@deprecated */
export enum ProductLevel {
	PRO_SMILE = "professionalx",
	PRO_LITE = "professionallite",
	PRO_FULL = "professionalfull",
	CAM = "cam", // MEMOTAIN
	LEGACY_CA_AESTHETIC = "aesthetic", // LEGACY
	LEGACY_CA_ESSENTIAL = "essential", // LEGACY
	LEGACY_PROFESSIONAL = "professional", // LEGACY (last modified treatment in 2019)
}

export const FunctionALPackageSize = {
	BASE_PACKAGE_SIZE: 20,
};

export enum ProductKey {
	PRO_SMILE_SCHIENE = "ca-schiene-professionalx",
	PRO_LITE_SCHIENE = "ca-schiene-professionallite",
	PRO_FULL_SCHIENE = "ca-schiene-professionalfull",
	PRO_SMILE_DATEN = "ca-daten-professionalx",
	PRO_LITE_DATEN = "ca-daten-professionallite",
	PRO_FULL_DATEN = "ca-daten-professionalfull",
	PRO_CASE_ASSESSMENT_DATEN = "professional-case-assessment-daten",
	PRO_CASE_ASSESSMENT_SCHIENE = "professional-case-assessment-schiene",
	MEMOTAIN = "cad-cam",
	MEMOTAIN_DISCOUNTED = "memotain-discounted",
	MEMOTAIN_RETENTIONSSCHIENE_BUNDLE = "memotain-retentionsschiene-bundle",
	RETENTIONSSCHIENE = "retentionsschiene",
	RETENTIONSSCHIENE_BULK_3x2 = "retentionsschiene-bulk-3x2",
	RETENTIONSSCHIENE_BULK_2x1 = "retentionsschiene-bulk-2x1",
	RETENTIONSSCHIENE_RETAINER_BUNDLE = "retentionsschiene-retainer-bundle", //not a "real" product, but an addendum for memotain or peek retainer bundles
	FUNCTIONAL_SCHIENE_ASSESSMENT = "functional-schiene-assessment", //there may be a 'data-variant' later, but not yet
	FUNCTIONAL_SCHIENE_NEO = "functional-schiene-neo",
	FUNCTIONAL_SCHIENE_PLUS = "functional-schiene-plus",
	FUNCTIONAL_SCHIENE_MAX = "functional-schiene-max",
	AOA_BULK_ORDER = "bulk-order",
	MICHIGAN_SPLINT = "michigan-splint",
	PRE_ALIGNER = "pre-aligner",
	PEEK_RETAINER = "peek-retainer",
	PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE = "peek-retainer-retentionsschiene-bundle",
	PROFESSIONAL_PRINT_SERVICE = "professional-print-service", //not a "real" product, but an addendum for professional data variants
	LEGACY_ESSENTIAL_DATEN = "ca-daten-essential",
	LEGACY_AESTHETIC_DATEN = "ca-daten-aesthetic",
	LEGACY_ESSENTIAL_TIEFZIEH = "ca-tiefzieh-essential",
	LEGACY_AESTHETIC_TIEFZIEH = "ca-tiefzieh-aesthetic",
	LEGACY_ESSENTIAL_SCHIENE = "ca-schiene-essential",
	LEGACY_AESTHETIC_SCHIENE = "ca-schiene-aesthetic",
}

export const PROFESSIONAL_PRODUCT_KEYS = [
	ProductKey.PRO_FULL_SCHIENE,
	ProductKey.PRO_FULL_DATEN,
	ProductKey.PRO_LITE_SCHIENE,
	ProductKey.PRO_LITE_DATEN,
	ProductKey.PRO_SMILE_SCHIENE,
	ProductKey.PRO_SMILE_DATEN,
];

export const FUNCTIONAL_PRODUCT_KEYS = [
	ProductKey.FUNCTIONAL_SCHIENE_NEO,
	ProductKey.FUNCTIONAL_SCHIENE_PLUS,
	ProductKey.FUNCTIONAL_SCHIENE_MAX,
];

export const MEMOTAIN_PRODUCT_KEYS = [
	ProductKey.MEMOTAIN,
	ProductKey.MEMOTAIN_DISCOUNTED,
	ProductKey.MEMOTAIN_RETENTIONSSCHIENE_BUNDLE,
];

export const PEEK_RETAINER_PRODUCT_KEYS = [
	ProductKey.PEEK_RETAINER,
	ProductKey.PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE,
];

/** Set of all lingual retainer type products: MEMOTAIN and MEMOTAIN PEEK 3D */
export const LINGUAL_RETAINER_PRODUCT_KEYS = [...MEMOTAIN_PRODUCT_KEYS, ...PEEK_RETAINER_PRODUCT_KEYS];

export const LINGUAL_RETAINER_BUNDLE_PRODUCT_KEYS = [
	ProductKey.MEMOTAIN_RETENTIONSSCHIENE_BUNDLE,
	ProductKey.PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE,
];

export const LEGACY_ESSENTIAL_AESTHETIC_PRODUCT_KEYS = [
	ProductKey.LEGACY_ESSENTIAL_DATEN,
	ProductKey.LEGACY_AESTHETIC_DATEN,
	ProductKey.LEGACY_ESSENTIAL_TIEFZIEH,
	ProductKey.LEGACY_AESTHETIC_TIEFZIEH,
	ProductKey.LEGACY_ESSENTIAL_SCHIENE,
	ProductKey.LEGACY_AESTHETIC_SCHIENE,
];

export const PROFESSIONAL_PRODUCT_SCHIENE_KEYS = [
	ProductKey.PRO_FULL_SCHIENE,
	ProductKey.PRO_LITE_SCHIENE,
	ProductKey.PRO_SMILE_SCHIENE,
];

export const PROFESSIONAL_PRODUCT_DATA_KEYS = [
	ProductKey.PRO_FULL_DATEN,
	ProductKey.PRO_LITE_DATEN,
	ProductKey.PRO_SMILE_DATEN,
];

export const PROFESSIONAL_CASE_ASSESSMENT_PRODUCT_KEYS = [
	ProductKey.PRO_CASE_ASSESSMENT_DATEN,
	ProductKey.PRO_CASE_ASSESSMENT_SCHIENE,
];

export const RETENTIONSSCHIENE_PRODUCT_DATA_KEYS = [
	ProductKey.RETENTIONSSCHIENE,
	ProductKey.RETENTIONSSCHIENE_BULK_3x2,
	ProductKey.RETENTIONSSCHIENE_BULK_2x1,
];

/**
 * Retainer products between which an internal user may switch the ordered product
 * via the TM Admin (see ticket #1274). A switch is allowed in any direction
 * between all of these keys.
 */
export const RETAINER_SWITCHABLE_PRODUCT_KEYS = [
	ProductKey.MEMOTAIN,
	ProductKey.MEMOTAIN_RETENTIONSSCHIENE_BUNDLE,
	ProductKey.PEEK_RETAINER,
	ProductKey.PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE,
	ProductKey.RETENTIONSSCHIENE,
	ProductKey.RETENTIONSSCHIENE_BULK_3x2,
	ProductKey.RETENTIONSSCHIENE_BULK_2x1,
];

/** Retainer products that can be split off from a multi-jaw product into two single-jaw products. */
export const RETAINER_SPLITTABLE_PRODUCT_KEYS = RETAINER_SWITCHABLE_PRODUCT_KEYS.filter(
	(key) => ![ProductKey.RETENTIONSSCHIENE_BULK_3x2].includes(key)
);

export const SAP_UPDATABLE_PRODUCTS = [
	ProductKey.MEMOTAIN,
	ProductKey.MEMOTAIN_RETENTIONSSCHIENE_BUNDLE,
	ProductKey.MEMOTAIN_DISCOUNTED,
	ProductKey.PEEK_RETAINER,
	ProductKey.PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE,
	ProductKey.RETENTIONSSCHIENE,
	ProductKey.RETENTIONSSCHIENE_BULK_2x1,
	ProductKey.RETENTIONSSCHIENE_BULK_3x2,
	ProductKey.MICHIGAN_SPLINT,
	...PROFESSIONAL_PRODUCT_KEYS,
];

export const SAP_CREATE_ORDER_ELIGIBLE_PRODUCTS = [
	ProductKey.MEMOTAIN,
	ProductKey.MEMOTAIN_DISCOUNTED,
	ProductKey.MEMOTAIN_RETENTIONSSCHIENE_BUNDLE,
	ProductKey.RETENTIONSSCHIENE,
	ProductKey.RETENTIONSSCHIENE_BULK_2x1,
	ProductKey.RETENTIONSSCHIENE_BULK_3x2,
	ProductKey.PEEK_RETAINER,
	ProductKey.PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE,
	ProductKey.MICHIGAN_SPLINT,
];

/**
 * Very old aligner product keys that were used before the final
 * essential and aesthetic products existed.
 * These products can still be displayed in TM Admin
 */
export const LEGACY_ALIGNER_PRODUCT_KEYS = [
	"ca1-aesthetic", //tiefzieh-variante
	"ca2-aesthetic", //schienen-variante
	"ca3-aesthetic", //daten-variante
	"ca1-essential", //tiefzieh-variante
	"ca2-essential", //schienen-variante
	"ca3-essential", //daten-variante
	"be-schiene-aesthetic",
	"be-schiene-essential",
	"be-schiene-professionallite",
	"be-schiene-professionalfull",
	"ca2-professional",
];

/**
 * Product keys that still exist in the mongoDB but were discontinued
 * a long a time ago.
 * This array is more like an archive than of functional use.
 */
export const LEGACY_MISCELLANEOUS_PRODUCT_KEYS = [
	"vector40",
	"lingualbond",
	"special-line",
	"damon-q",
	"chic",
	"experience-ceramic",
	"experience-metal",
];

export enum QuestionnaireScopeLevel {
	SHORT, //former 'level1'
	COMPLETE, //former 'level2'
	ABBREVIATED, //former 'level3'
}

/** @deprecated do not use */
/** The default treatment name of the API-variant of the MEMOTAIN product. */
export const MEMOTAIN_PARTNER_API_TREATMENT_NAME = "Service API Memotain Preparation";

/** @deprecated do not use */
/** The 'virtual' product info title of the API-variant of the MEMOTAIN product. */
export const MEMOTAIN_DISTRIBUTION_PRODUCT_TITLE = "MEMOTAIN (DISTRIBUTION)";

/** @deprecated do not use */
/** The default treatment name of an API-variant of a MEMOTAIN bulk order (multiorder). */
export const MEMOTAIN_BULK_ORDER_PARTNER_API_TREATMENT_NAME = "Service API Distribution Memotain Bulk Order";

/** @deprecated do not use */
/** The 'virtual' product info title of the API-variant of the MEMOTAIN bulk order. */
export const MEMOTAIN_BULK_ORDER_DISTRIBUTION_PRODUCT_TITLE = "MEMOTAIN BULK ORDER (DISTRIBUTION)";

export const DATEN_VARIANTE = "Daten-Variante";
export const SCHIENEN_VARIANTE = "Schienen-Variante";

export interface ProductInfo {
	name: string;
	productSelectionName?: string; //name that is displayed in product selection
	variantName?: string;
	descriptionKey?: string;
	basePackageSize?: number;
	maxBasePackages?: number;
	maxRefinementStepsPerPackage?: number;
	corrections?: number[];
	toothArea?: string;
	questionnaireScopeLevel?: QuestionnaireScopeLevel;
}

export const ProductInfoForKey: Record<ProductKey, ProductInfo> = {
	[ProductKey.PRO_SMILE_SCHIENE]: {
		name: "PROFESSIONAL SMILE",
		productSelectionName: "Professional Smile",
		variantName: SCHIENEN_VARIANTE,
		basePackageSize: 10,
		maxBasePackages: 1,
		corrections: [5],
		toothArea: ToothArea.FIVE_FIVE,
		questionnaireScopeLevel: QuestionnaireScopeLevel.SHORT,
	},
	[ProductKey.PRO_LITE_SCHIENE]: {
		name: "PROFESSIONAL LITE",
		productSelectionName: "Professional Lite",
		variantName: SCHIENEN_VARIANTE,
		basePackageSize: 20,
		maxBasePackages: 1,
		corrections: [10],
		toothArea: ToothArea.SEVEN_SEVEN,
		questionnaireScopeLevel: QuestionnaireScopeLevel.COMPLETE,
	},
	[ProductKey.PRO_FULL_SCHIENE]: {
		name: "PROFESSIONAL FULL",
		productSelectionName: "Professional Full",
		variantName: SCHIENEN_VARIANTE,
		basePackageSize: 20,
		maxBasePackages: 3,
		corrections: [
			10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10,
			10,
		],
		toothArea: ToothArea.SEVEN_SEVEN,
		questionnaireScopeLevel: QuestionnaireScopeLevel.COMPLETE,
	},
	[ProductKey.PRO_SMILE_DATEN]: {
		name: "PROFESSIONAL SMILE",
		productSelectionName: "Professional Smile",
		variantName: DATEN_VARIANTE,
		basePackageSize: 10,
		maxBasePackages: 1,
		corrections: [5],
		toothArea: ToothArea.FIVE_FIVE,
		questionnaireScopeLevel: QuestionnaireScopeLevel.SHORT,
	},
	[ProductKey.PRO_LITE_DATEN]: {
		name: "PROFESSIONAL LITE",
		productSelectionName: "Professional Lite",
		variantName: DATEN_VARIANTE,
		basePackageSize: 20,
		maxBasePackages: 1,
		corrections: [10],
		toothArea: ToothArea.SEVEN_SEVEN,
		questionnaireScopeLevel: QuestionnaireScopeLevel.COMPLETE,
	},
	[ProductKey.PRO_FULL_DATEN]: {
		name: "PROFESSIONAL FULL",
		productSelectionName: "Professional Full",
		variantName: DATEN_VARIANTE,
		basePackageSize: 20,
		maxBasePackages: 3,
		corrections: [
			10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10,
			10,
		],
		toothArea: ToothArea.SEVEN_SEVEN,
		questionnaireScopeLevel: QuestionnaireScopeLevel.COMPLETE,
	},
	[ProductKey.PRO_CASE_ASSESSMENT_SCHIENE]: {
		name: "Case Assessment",
		productSelectionName: "CaseAssessment",
		descriptionKey: "CaseAssessmentDescription",
		variantName: SCHIENEN_VARIANTE,
		toothArea: ToothArea.SEVEN_SEVEN,
		questionnaireScopeLevel: QuestionnaireScopeLevel.COMPLETE,
	},
	[ProductKey.PRO_CASE_ASSESSMENT_DATEN]: {
		name: "Case Assessment",
		productSelectionName: "CaseAssessment",
		descriptionKey: "CaseAssessmentDescription",
		variantName: DATEN_VARIANTE,
		toothArea: ToothArea.SEVEN_SEVEN,
		questionnaireScopeLevel: QuestionnaireScopeLevel.COMPLETE,
	},
	[ProductKey.MEMOTAIN]: {
		name: "MEMOTAIN®",
		productSelectionName: "MemotainProductSelectionName",
		descriptionKey: "MemotainDescription",
	},
	[ProductKey.MEMOTAIN_DISCOUNTED]: {
		name: "MEMOTAIN®",
		descriptionKey: "MemotainDescription", // Reuse the regular memotain description.
	},
	[ProductKey.MEMOTAIN_RETENTIONSSCHIENE_BUNDLE]: {
		name: "MEMOTAIN®",
		variantName: "+ Clear (Bundle)",
		descriptionKey: "MemotainDescription", // Reuse the regular memotain description.
	},
	[ProductKey.RETENTIONSSCHIENE]: {
		name: "MEMOTAIN®",
		variantName: "Clear",
		productSelectionName: "RetentionsschieneProductSelectionName",
		toothArea: ToothArea.SEVEN_SEVEN,
		descriptionKey: "RetentionsschieneDescription",
	},
	[ProductKey.RETENTIONSSCHIENE_BULK_3x2]: {
		name: "MEMOTAIN®",
		variantName: "Clear 3x2",
		toothArea: ToothArea.SEVEN_SEVEN,
		descriptionKey: "RetentionsschieneDescription", // Reuse the regular retentionsschiene description.
	},
	[ProductKey.RETENTIONSSCHIENE_BULK_2x1]: {
		name: "MEMOTAIN®",
		variantName: "Clear 2x1",
		toothArea: ToothArea.SEVEN_SEVEN,
		descriptionKey: "RetentionsschieneDescription", // Reuse the regular retentionsschiene description.
	},
	[ProductKey.PEEK_RETAINER]: {
		name: "MEMOTAIN®",
		variantName: "Peek 3D",
		productSelectionName: "PeekRetainerProductSelectionName",
		descriptionKey: "PeekRetainerDescription",
	},
	[ProductKey.PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE]: {
		name: "MEMOTAIN®",
		variantName: "Peek 3D + Clear (Bundle)",
		descriptionKey: "PeekRetainerDescription", // Reuse the regular peek retainer description.
	},
	[ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT]: {
		name: "FUNCTIONAL",
		productSelectionName: "ALIGNER",
		descriptionKey: "FunctionALDescription",
		toothArea: ToothArea.SEVEN_SEVEN,
	},
	[ProductKey.FUNCTIONAL_SCHIENE_NEO]: {
		name: "FUNCTIONAL Neo",
		productSelectionName: "FUNCTIONAL Neo",
		descriptionKey: "FunctionALDescription",
		maxBasePackages: 1,
		maxRefinementStepsPerPackage: 10,
		toothArea: ToothArea.SEVEN_SEVEN,
	},
	[ProductKey.FUNCTIONAL_SCHIENE_PLUS]: {
		name: "FUNCTIONAL Plus",
		productSelectionName: "FUNCTIONAL Plus",
		descriptionKey: "FunctionALDescription",
		maxBasePackages: 2,
		maxRefinementStepsPerPackage: 20,
		toothArea: ToothArea.SEVEN_SEVEN,
	},
	[ProductKey.FUNCTIONAL_SCHIENE_MAX]: {
		name: "FUNCTIONAL Max",
		productSelectionName: "FUNCTIONAL Max",
		descriptionKey: "FunctionALDescription",
		maxRefinementStepsPerPackage: 20,
		toothArea: ToothArea.SEVEN_SEVEN,
	},
	[ProductKey.AOA_BULK_ORDER]: {
		name: "MEMOTAIN BULK ORDER",
	},
	[ProductKey.MICHIGAN_SPLINT]: {
		name: "Michigan splint",
		productSelectionName: "MichiganSplintProductSelectionName",
		toothArea: ToothArea.SEVEN_SEVEN,
		descriptionKey: "MichiganSplintDescription",
	},
	[ProductKey.PRE_ALIGNER]: {
		name: "Pre Aligner",
	},
	[ProductKey.PROFESSIONAL_PRINT_SERVICE]: {
		name: "Professional Print Service",
	},
	[ProductKey.RETENTIONSSCHIENE_RETAINER_BUNDLE]: {
		name: "Retentionsschiene Memotain-/Peekretainer-Bundle",
	},
	[ProductKey.LEGACY_ESSENTIAL_DATEN]: {
		name: "ESSENTIAL",
		variantName: "Daten-Variante",
	},
	[ProductKey.LEGACY_AESTHETIC_DATEN]: {
		name: "AESTHETIC",
		variantName: "Daten-Variante",
	},
	[ProductKey.LEGACY_ESSENTIAL_TIEFZIEH]: {
		name: "ESSENTIAL",
		variantName: "Tiefzieh-Variante",
	},
	[ProductKey.LEGACY_AESTHETIC_TIEFZIEH]: {
		name: "AESTHETIC",
		variantName: "Tiefzieh-Variante",
	},
	[ProductKey.LEGACY_ESSENTIAL_SCHIENE]: {
		name: "ESSENTIAL",
		variantName: "Schienen-Variante",
	},
	[ProductKey.LEGACY_AESTHETIC_SCHIENE]: {
		name: "AESTHETIC",
		variantName: "Schienen-Variante",
	},
};
